/**
 * Job Reorder WebSocket Handler Component
 *
 * This component handles real-time job reordering updates via YJS collaboration service.
 * It subscribes to job reorder events and updates the JobPasteStore accordingly.
 *
 * Features:
 * - Listens for job_reorder_update messages from other users
 * - Listens for job_reorder_confirmed messages for current user
 * - Updates JobPasteStore with new job serial numbers
 * - Shows notifications to users about reorder events with specific job details
 * - Handles both main data and cloned data during edit mode
 */

import { useStore } from 'hooks/useStore';
import { useYJSCollaboration } from 'hooks/useYJSCollaboration';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { toast } from 'react-toastify';

/**
 * Formats a user identifier for display in notifications
 * If the identifier looks like an email, extracts the name part
 * @param userIdentifier - The user identifier (name or email)
 * @returns Formatted display name
 */
const formatUserDisplayName = (userIdentifier: string): string => {
  if (!userIdentifier) return 'Unknown User';

  // If it contains '@', it's likely an email - extract the part before @
  if (userIdentifier.includes('@')) {
    return userIdentifier.split('@')[0];
  }

  return userIdentifier;
};

/**
 * Formats a list of job IDs for display in notifications
 * Handles different scenarios for readability
 * @param jobIds - Array of job IDs that were reordered
 * @returns Formatted string showing affected jobs
 */
const formatJobList = (jobIds: string[]): string => {
  const count = jobIds.length;

  if (count === 0) return '';
  if (count === 1) return `job ${jobIds[0]}`;
  if (count === 2) return `jobs ${jobIds[0]} and ${jobIds[1]}`;
  if (count === 3) return `jobs ${jobIds[0]}, ${jobIds[1]}, and ${jobIds[2]}`;

  // For more than 3 jobs, show first 2 and indicate how many more
  const remaining = count - 2;
  return `jobs ${jobIds[0]}, ${jobIds[1]}, and ${remaining} other${remaining === 1 ? '' : 's'}`;
};

const JobReorderWebSocketHandler: React.FC = () => {
  const { JobPasteStore, UserStore } = useStore();
  const { onJobReorderUpdate, onJobReorderConfirmation } = useYJSCollaboration();

  useEffect(() => {
    // Subscribe to job reorder updates from other users
    const unsubscribeReorderUpdate = onJobReorderUpdate(data => {
      // Don't process updates from the current user (they already have the changes)
      const currentUserName = UserStore.userMetadata?.userInfo?.email;
      if (data.reorderedBy === currentUserName) {
        return;
      }

      // Update JobPasteStore with the new job order
      JobPasteStore.handleJobReorderFromWebSocket(data.roomName, data.jobSerials, data.reorderedBy);

      // Extract job IDs and create notification message
      const jobIds = Object.keys(data.jobSerials).sort(); // Sort for consistent display
      const displayName = formatUserDisplayName(data.reorderedBy);
      const jobListText = formatJobList(jobIds);

      const message = `${displayName} reordered ${jobListText} in room "${data.roomName}"`;

      toast.info(message, {
        toastId: `job-reorder-${data.roomName}-${data.reorderedBy}-${Date.now()}`,
        autoClose: 5000,
        position: 'bottom-right'
      });
    });

    // Subscribe to job reorder confirmations for the current user
    const unsubscribeReorderConfirmation = onJobReorderConfirmation(data => {
      // console.log('✅ Received job reorder confirmation:', data);
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeReorderUpdate();
      unsubscribeReorderConfirmation();
    };
  }, [onJobReorderUpdate, onJobReorderConfirmation, JobPasteStore, UserStore]);

  // This component doesn't render anything - it's just for side effects
  return null;
};

export default observer(JobReorderWebSocketHandler);
